import React from 'react';
import { Box, Inscription, List, PaginationBar, SearchInput } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FundsHistoryTransaction } from '../../UnappliedFunds.types';

import FundsHistoryItem from './FundsHistoryItem';

const StyledSearchInput = styled(SearchInput)`
  && {
    padding-right: ${({ theme }) => theme.spacings.spacing4} !important;
  }
`;

interface Props {
  transactions: FundsHistoryTransaction[];
  total: number;
  isLoading: boolean;
  hasActiveSearch: boolean;
  page: number;
  perPage: number;
  onChangePage: (page: number) => void;
  onChangePerPage: (perPage: number) => void;
  searchString: string;
  onChangeSearch: (value: string) => void;
}

const FundsHistory: React.FC<Props> = ({
  transactions,
  total,
  isLoading,
  hasActiveSearch,
  page,
  perPage,
  onChangePage,
  onChangePerPage,
  searchString,
  onChangeSearch,
}) => {
  const isEmpty = !isLoading && total === 0;
  const hasNoTransactions = isEmpty && !hasActiveSearch;

  return (
    <Box data-testid="funds-history" pad="spacing24" background="module" round="container1Round">
      <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing24' }}>
        <Inscription weight="bold" color="textBody">
          Invoices: <Inscription data-testid="funds-history_total">{total}</Inscription>
        </Inscription>
        <Box width="260px">
          <StyledSearchInput
            testId="funds-history_search"
            value={searchString}
            onChange={onChangeSearch}
            placeholder="Search description or amount"
            disabled={hasNoTransactions}
          />
        </Box>
      </Box>

      <List>
        {!isEmpty && (
          <List.Header>
            <List.Header.Item flex={2}>Amount</List.Header.Item>
            <List.Header.Item flex={2}>Date</List.Header.Item>
            <List.Header.Item flex={4}>Description</List.Header.Item>
          </List.Header>
        )}

        {isLoading && <List.Preloader testId="funds-history_preloader" count={3} />}

        {hasNoTransactions && (
          <Box pad={{ vertical: 'spacing24' }} align="center">
            <Inscription weight="bold" data-testid="funds-history_empty-text">
              There are no unpaid invoices
            </Inscription>
          </Box>
        )}

        {isEmpty && hasActiveSearch && <List.Placeholder testId="funds-history_no-results" />}

        {!isLoading && transactions.map((transaction) => <FundsHistoryItem key={transaction.id} {...transaction} />)}
      </List>

      <PaginationBar
        page={page}
        total={total}
        pageSize={perPage}
        onChangePage={onChangePage}
        onChangePageSize={onChangePerPage}
      />
    </Box>
  );
};

export default FundsHistory;
